import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Pin, PinOff, Star, UserCircle } from "lucide-react";
import { featuredList, featuredSetOrder } from "@/services/firebase/artistHub";
import { getActorPhotoUrl } from "@/services/firebase/actor";
import { FeaturedArtist, featuredPreview, movePinned, pinnedIds, sameOrder } from "@/lib/featured";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminEmptyState,
  AdminErrorAlert,
  AdminStatusBadge,
} from "@/components/admin-ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const QUERY_KEY = ["artist-featured"];

function Avatar({ artist }: { artist: FeaturedArtist }) {
  const src = artist.avatarUrl || getActorPhotoUrl(artist.avatarPath || undefined);
  if (!src) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
        <UserCircle className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }
  return <img src={src} alt="" width={40} height={40} loading="lazy" className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover" />;
}

/**
 * 精選藝人排序: App 首頁「精選藝人」與星空推薦藝人 show the first `visibleCount` published artists, pinned ones first
 * (featuredOrder 1..n), then by followers. Staff reorder locally and save the pinned list in one call.
 */
export default function FeaturedArtistsPage() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: featuredList, retry: false });
  const saved = useMemo(() => pinnedIds(query.data?.items ?? []), [query.data]);
  const [pinned, setPinned] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setPinned(saved), [saved]);

  const save = useMutation({
    mutationFn: () => featuredSetOrder(pinned),
    onSuccess: (page) => {
      client.setQueryData(QUERY_KEY, page);
      void client.invalidateQueries({ queryKey: ["artist-admin"] });
    },
  });

  const items = query.data?.items ?? [];
  const visibleCount = query.data?.visibleCount ?? 10;
  const maxPinned = query.data?.maxPinned ?? 30;
  const preview = useMemo(() => featuredPreview(items, pinned), [items, pinned]);
  const dirty = !sameOrder(pinned, saved);
  const busy = save.isPending;

  const header = (
    <AdminPageHeader
      title="精選藝人排序"
      subtitle={`App 首頁「精選藝人」與星空推薦藝人只顯示前 ${visibleCount} 位：先依置頂順序，其餘依追蹤數。`}
      loading={query.isFetching}
      onRefresh={dirty ? undefined : () => void query.refetch()}
    />
  );

  if (query.isPending) {
    return (
      <AdminPage>
        {header}
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }
  if (query.isError) {
    return (
      <AdminPage>
        {header}
        <AdminErrorAlert message={`載入精選藝人失敗：${query.error.message}`} />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      {header}
      {save.isError && <AdminErrorAlert message={`儲存失敗：${save.error.message}`} onDismiss={() => save.reset()} />}

      {items.length === 0 ? (
        <AdminEmptyState
          icon={<Star className="h-5 w-5" />}
          title="還沒有已公開的藝人"
          description="在演員編輯頁發布藝人專頁後，就能在這裡排序。"
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>App 顯示順序預覽</CardTitle>
            <CardDescription>
              置頂最多 {maxPinned} 位。取消置頂的藝人會回到依追蹤數排序的位置。儲存後 App 下次載入就會套用。
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ol>
              {preview.map((artist, index) => {
                const pinIndex = artist.pinnedRank === null ? -1 : artist.pinnedRank - 1;
                return (
                  <li key={artist.artistId}>
                    {index === visibleCount && (
                      <div className="flex items-center gap-3 border-y border-dashed bg-muted/40 px-6 py-2 text-xs text-muted-foreground">
                        以下不會出現在 App 精選藝人
                      </div>
                    )}
                    <div className={`flex flex-wrap items-center gap-3 border-b px-6 py-3 last:border-b-0 ${index >= visibleCount ? "opacity-60" : ""}`}>
                      <span className="w-6 text-right text-sm tabular-nums text-muted-foreground">{index + 1}</span>
                      <Avatar artist={artist} />
                      <div className="min-w-0 flex-1">
                        <Link to={`/actors/${artist.artistId}/edit`} className="block truncate text-sm font-medium text-foreground hover:underline">
                          {artist.displayName || artist.artistId}
                        </Link>
                        <span className="text-xs text-muted-foreground">{artist.followerCount.toLocaleString("zh-TW")} 位追蹤</span>
                      </div>
                      {artist.pinnedRank !== null ? (
                        <AdminStatusBadge label={`置頂 ${artist.pinnedRank}`} tone="purple" />
                      ) : (
                        <AdminStatusBadge label="依追蹤數" tone="neutral" />
                      )}
                      <div className="flex items-center gap-1">
                        {artist.pinnedRank !== null ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`${artist.displayName} 往上移`}
                              disabled={busy || pinIndex === 0}
                              onClick={() => setPinned(movePinned(pinned, pinIndex, -1))}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`${artist.displayName} 往下移`}
                              disabled={busy || pinIndex === pinned.length - 1}
                              onClick={() => setPinned(movePinned(pinned, pinIndex, 1))}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              onClick={() => setPinned(pinned.filter((id) => id !== artist.artistId))}
                            >
                              <PinOff className="mr-1 h-4 w-4" />
                              取消置頂
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy || pinned.length >= maxPinned}
                            onClick={() => setPinned([...pinned, artist.artistId])}
                          >
                            <Pin className="mr-1 h-4 w-4" />
                            置頂
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      )}

      {(dirty || save.isSuccess) && items.length > 0 && (
        <div className="sticky bottom-0 mt-6 flex flex-wrap items-center justify-end gap-3 border-t bg-background/95 py-4 backdrop-blur">
          <span role="status" className="mr-auto text-sm text-muted-foreground">
            {dirty ? "排序有未儲存的變更" : "排序已儲存"}
          </span>
          {dirty && (
            <>
              <Button variant="outline" disabled={busy} onClick={() => setPinned(saved)}>
                復原
              </Button>
              <Button disabled={busy} onClick={() => setConfirming(true)}>
                {busy ? "儲存中…" : "儲存排序"}
              </Button>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirming}
        title="儲存精選藝人排序"
        message={`App 首頁會改成以下前 ${Math.min(visibleCount, preview.length)} 位，下次載入就會套用。`}
        extra={
          <ol className="mt-2 list-decimal space-y-0.5 pl-6 text-sm">
            {preview.slice(0, visibleCount).map((a) => (
              <li key={a.artistId}>
                {a.displayName || a.artistId}
                {a.pinnedRank !== null ? "（置頂）" : ""}
              </li>
            ))}
          </ol>
        }
        confirmText="儲存"
        onConfirm={() => {
          setConfirming(false);
          save.mutate();
        }}
        onCancel={() => setConfirming(false)}
      />
    </AdminPage>
  );
}
